<template>
  <div class="wheel-box">
    <!-- 高亮扇形区域，仅显示 currentIndex 对应扇区 -->
    <div class="segments">
      <div class="highlight" :style="getHighlightStyle()" />
    </div>

    <!-- 奖品图标容器 -->
    <div class="prizes">
      <div
        v-for="(prize, index) in prizeList"
        :key="index"
        class="prize-item"
        :style="getPrizeStyle(index)"
      >
        <img
          :src="prize.main_img"
          class="prize-img"
          :style="getPrizeImageStyle(index)"
        />
      </div>
    </div>

    <!-- GO 指针图 -->
    <img
      :src="pointerImg"
      class="pointer"
      :style="getPointerStyle()"
    />
  </div>
</template>

<script setup>
import { ref } from 'vue';

// props 定义：total 扇区数，prizeList 奖品数组，pointerImg 指针图，onFinished 完成回调
const props = defineProps({
  total: { type: Number, default: 8 },
  prizeList: { type: Array, required: true },
  pointerImg: { type: String, required: true },
  onFinished: { type: Function, default: () => {} }
});

// 内部状态
const pointerAngle = ref(0);        // 指针角度
const currentIndex = ref(-1);       // 高亮扇区索引
const anglePer = 360 / props.total;
let rotateRaf = null;               // requestAnimationFrame id
let startTime = 0;                  // accelerate start timestamp
let lastTime = 0;                   // last frame timestamp

// 缓动函数（慢-快-慢）
const easeInOutQuad = t => (t < 0.5 ? 2*t*t : -1 + (4 - 2*t)*t);

/**
 * 启动分段匀速动画：2s 加速到匀速，然后保持匀速
 */
function startRotate() {
  if (rotateRaf) return; // 已在旋转中
  // 配置加速阶段和速度
  const accelDuration = 2000;    // 加速时长 ms
  const minSpeed = 0.05;         // 最小速度 deg/ms
  const maxSpeed = 0.3;          // 最大匀速 deg/ms

  startTime = performance.now();
  lastTime = startTime;

  function loop(now) {
    const elapsed = now - startTime;
    // 计算当前速度
    let speed;
    if (elapsed < accelDuration) {
      const t = elapsed / accelDuration;
      const eased = easeInOutQuad(t);
      speed = minSpeed + (maxSpeed - minSpeed) * eased;
    } else {
      speed = maxSpeed;
    }
    // 计算增量并更新角度
    const delta = now - lastTime;
    pointerAngle.value = (pointerAngle.value + speed * delta) % 360;
    currentIndex.value = Math.floor(pointerAngle.value / anglePer);
    lastTime = now;
    rotateRaf = requestAnimationFrame(loop);
  }
  rotateRaf = requestAnimationFrame(loop);
}

/**
 * 停止匀速并缓动到目标扇区
 * @param {number} targetIdx 目标扇区索引
 * @param {{laps?: number, duration?: number}} options
 */
function stopRotate(targetIdx, options = {}) {
  // 先取消匀速循环
  if (rotateRaf) cancelAnimationFrame(rotateRaf);
  rotateRaf = null;

  // 起始角度不变
  const start = pointerAngle.value % 360;
  const laps = options.laps !== undefined ? options.laps : 5;
  const duration = options.duration !== undefined ? options.duration : 2000;
  const targetAngle = laps * 360 + targetIdx * anglePer + anglePer/2;
  const beginTime = performance.now();

  function animate(now) {
    const t = Math.min((now - beginTime) / duration, 1);
    const eased = easeInOutQuad(t);
    pointerAngle.value = start + (targetAngle - start) * eased;
    currentIndex.value = Math.floor((pointerAngle.value % 360) / anglePer);
    if (t < 1) {
      requestAnimationFrame(animate);
    } else {
      pointerAngle.value = targetAngle;
      currentIndex.value = targetIdx;
      props.onFinished(targetIdx);
    }
  }
  requestAnimationFrame(animate);
}

/**
 * 重置指针状态：停止旋转并归位到初始位置、清除高亮
 */
function resetRotate() {
  if (rotateRaf) cancelAnimationFrame(rotateRaf);
  rotateRaf = null;
  pointerAngle.value = 0;
  currentIndex.value = -1;
}

// 对外暴露接口
defineExpose({ startRotate, stopRotate, resetRotate });

/**
 * 计算高亮样式，只高亮 currentIndex 对应分片
 */
const getHighlightStyle = () => {
  const idx = currentIndex.value;
  if (idx < 0) return { background: 'transparent' };
  const start = idx * anglePer;
  const end = start + anglePer;
  return {
    background: `conic-gradient(transparent 0deg ${start}deg, rgba(255,223,77,0.4) ${start}deg ${end}deg, transparent ${end}deg 360deg)`
  };
};

// 计算奖品项定位
const getPrizeStyle = index => {
  const ang = anglePer * index + anglePer/2 - 90;
  const r = 90;
  const rad = ang * Math.PI / 180;
  const x = Math.cos(rad) * r;
  const y = Math.sin(rad) * r;
  return {
    position: 'absolute', top: '50%', left: '50%',
    transform: `translate(${x}px, ${y}px) translate(-50%,-50%) rotate(${ang+90}deg)`
  };
};

// 保持图标正立
const getPrizeImageStyle = index => ({
  width: '46px', height: '46px',
  transform: `rotate(${- (anglePer*index + anglePer/2)}deg)`
});

// 计算指针样式
const getPointerStyle = () => ({
  transform: `translate(-50%,-50%) rotate(${pointerAngle.value}deg)`
});
</script>

<style scoped>
.wheel-box { position: relative; width: 300px; height: 300px; margin: auto; }
.segments { position: absolute; inset: 0; z-index: 2; pointer-events: none; }
.highlight { width: 100%; height: 100%; border-radius: 50%; }
.prizes { position: absolute; inset: 0; z-index: 3; pointer-events: none; }
.prize-item { position: absolute; display: flex; justify-content: center; align-items: center; }
.prize-img { object-fit: contain; }
.pointer { position: absolute; top: 50%; left: 50%; width: 115px; height: 115px; transform-origin: center; z-index: 4; }
</style>
